import React, { useState, useEffect, useMemo } from 'react';
import { collection, doc, getDoc, getDocs, setDoc, query, where, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { raManader, MANAD_LABELS } from './budgetModel';
import { kontoGrupp } from '../sie';

// ── KOSTNADSANALYS ────────────────────────────────────────────────
// Benar ut bokföringens kostnader konto för konto: hur mycket som ligger
// stilla varje månad (fast) och hur mycket som svänger med verksamheten.
// Klassningen föreslås maskinellt ur månadsspridningen och kan skrivas
// över per konto — överskrivningarna sparas och gäller alla år.

const TYPER = {
  fast:     { label: 'Fast',     farg: '#1D9E75' },
  halvfast: { label: 'Halvfast', farg: '#2E6E8E' },
  rorlig:   { label: 'Rörlig',   farg: '#C98A2E' },
  engangs:  { label: 'Engångs',  farg: '#8A8A8A' },
};
const CYKEL = ['fast', 'halvfast', 'rorlig', 'engangs'];
const GRUPPVAL = [
  ['ovrigt', 'Övriga kostnader'],
  ['personal', 'Personal'],
  ['avskr', 'Avskrivningar'],
  ['finans', 'Finansiella'],
  ['vk', 'Varukostnader'],
];

// Maskinell klassning: hur många månader kontot finns på + hur jämnt det ligger.
// Exporterad för att budgetgeneratorn ska dela Övriga kostnader på fast/rörligt
// med exakt samma regel som analysvyn visar.
export function autoTyp(varden) {
  const med = varden.filter((v) => Math.abs(v) > 100).map(Math.abs);
  if (!med.length) return 'engangs';
  const kvot = Math.min(...med) > 0 ? Math.max(...med) / Math.min(...med) : 99;
  if (med.length >= 10 && kvot <= 2.5) return 'fast';
  if (med.length >= 8 && kvot <= 6) return 'halvfast';
  if (med.length <= 3) return 'engangs';
  return 'rorlig';
}

const kr = (v) => `${Math.round(v).toLocaleString('sv-SE')} kr`;
const tkr = (v) => Math.round(v / 1000).toLocaleString('sv-SE');

export default function KostnadsAnalys({ bolag }) {
  const [docs, setDocs] = useState(null);       // månadsdokument för bolaget
  const [kontoNamn, setKontoNamn] = useState({});
  const [overskrivet, setOverskrivet] = useState({}); // konto → typ (manuell)
  const [ra, setRa] = useState(null);
  const [grupp, setGrupp] = useState('ovrigt');
  const [oppet, setOppet] = useState(null);     // konto med utfälld månadsrad
  const [sparar, setSparar] = useState(false);

  useEffect(() => {
    let live = true;
    (async () => {
      const snap = await getDocs(query(collection(db, 'intel_bokforing'), where('bolag', '==', bolag)));
      const man = [];
      let namn = {};
      snap.forEach((d) => {
        const v = d.data();
        if (v.typ === 'meta') { namn = v.konton || {}; return; }
        if (v.typ === 'kontotyper') return;
        if (v.ym && Array.isArray(v.rader)) man.push(v);
      });
      const t = await getDoc(doc(db, 'intel_bokforing', `${bolag}_kontotyper`));
      if (!live) return;
      setDocs(man);
      setKontoNamn(namn);
      setOverskrivet(t.exists() ? (t.data().typer || {}) : {});
      setOppet(null);
    })().catch((e) => { console.error('kostnadsanalys:', e); setDocs([]); });
    return () => { live = false; };
  }, [bolag]);

  // Vilka räkenskapsår finns i datat?
  const raVal = useMemo(() => {
    if (!docs?.length) return [];
    const ar = new Set();
    for (const d of docs) {
      const y = Number(d.ym.slice(0, 4)), m = Number(d.ym.slice(4, 6));
      const start = m >= 7 ? y : y - 1; // RÅ startar i juli
      ar.add(`${String(start % 100).padStart(2, '0')}${String((start + 1) % 100).padStart(2, '0')}`);
    }
    return [...ar].sort().reverse();
  }, [docs]);

  useEffect(() => { if (raVal.length && !raVal.includes(ra)) setRa(raVal[0]); }, [raVal, ra]);

  const manader = useMemo(() => (ra ? raManader(ra) : []), [ra]);

  // Konto → månadsbelopp för valt RÅ och vald kontogrupp
  const rader = useMemo(() => {
    if (!docs || !ra) return [];
    const per = {};
    for (const d of docs) {
      const i = manader.indexOf(d.ym);
      if (i < 0) continue;
      for (const r of d.rader || []) {
        if (kontoGrupp(r.konto) !== grupp) continue;
        const k = per[r.konto] || (per[r.konto] = new Array(12).fill(0));
        k[i] += r.belopp;
      }
    }
    return Object.entries(per)
      .map(([konto, varden]) => {
        const total = varden.reduce((a, b) => a + b, 0);
        const antal = varden.filter((v) => Math.abs(v) > 100).length;
        const auto = autoTyp(varden);
        return {
          konto, varden, total, antal,
          snitt: antal ? total / antal : 0,
          typ: overskrivet[konto] || auto,
          manuell: !!overskrivet[konto],
        };
      })
      .filter((r) => Math.abs(r.total) > 1000)
      .sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
  }, [docs, ra, manader, grupp, overskrivet]);

  const perTyp = useMemo(() => {
    const s = { fast: 0, halvfast: 0, rorlig: 0, engangs: 0 };
    for (const r of rader) s[r.typ] += r.total;
    return s;
  }, [rader]);
  const totalt = rader.reduce((s, r) => s + r.total, 0);
  const perManad = useMemo(
    () => manader.map((_, i) => rader.reduce((s, r) => s + r.varden[i], 0)),
    [rader, manader]
  );
  const bundet = perTyp.fast + perTyp.halvfast;

  const cyklaTyp = async (konto, nuvarande) => {
    if (sparar) return;
    const i = CYKEL.indexOf(nuvarande);
    const ny = overskrivet[konto] && CYKEL[(i + 1) % 4] === autoTyp(rader.find((r) => r.konto === konto).varden)
      ? null // tillbaka till automatisk klassning
      : CYKEL[(i + 1) % 4];
    const nya = { ...overskrivet };
    if (ny) nya[konto] = ny; else delete nya[konto];
    setOverskrivet(nya);
    setSparar(true);
    try {
      await setDoc(doc(db, 'intel_bokforing', `${bolag}_kontotyper`), {
        bolag, typ: 'kontotyper', typer: nya,
        updatedAt: serverTimestamp(), updatedBy: auth.currentUser?.email || '',
      });
    } catch (e) { console.error('spara kontotyp:', e); }
    finally { setSparar(false); }
  };

  const chip = (aktiv) => ({
    background: aktiv ? '#44365C' : 'transparent', color: aktiv ? '#fff' : 'var(--text-secondary)',
    border: '1px solid var(--border)', borderRadius: 20, padding: '4px 12px',
    fontSize: 11.5, cursor: 'pointer', fontFamily: 'system-ui,sans-serif',
  });
  const td = { fontSize: 11.5, padding: '4px 8px 4px 0', fontFamily: 'system-ui,sans-serif', textAlign: 'right', whiteSpace: 'nowrap' };
  const th = { ...td, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)' };

  if (docs === null) {
    return <div className="card"><div className="card-title">📊 Kostnadsanalys</div><p style={{ fontSize: 12.5, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Läser bokföringen…</p></div>;
  }
  if (!docs.length) {
    return <div className="card"><div className="card-title">📊 Kostnadsanalys</div><p style={{ fontSize: 12.5, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Ingen bokföring uppladdad för det här bolaget än.</p></div>;
  }

  return (
    <div className="card">
      <div className="card-title">📊 Kostnadsanalys — vad ligger fast och vad svänger?</div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
        {raVal.map((r) => (
          <button key={r} onClick={() => { setRa(r); setOppet(null); }} style={chip(ra === r)}>RÅ {r.slice(0, 2)}/{r.slice(2)}</button>
        ))}
        <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)', margin: '0 4px' }} />
        {GRUPPVAL.map(([g, lbl]) => (
          <button key={g} onClick={() => { setGrupp(g); setOppet(null); }} style={chip(grupp === g)}>{lbl}</button>
        ))}
      </div>

      {!rader.length ? (
        <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Inga konton i den här gruppen för valt år.</p>
      ) : (
        <>
          {/* Summering per typ */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8, marginBottom: 12 }}>
            {CYKEL.map((t) => (
              <div key={t} style={{ border: '1px solid var(--border)', borderLeft: `3px solid ${TYPER[t].farg}`, borderRadius: 8, padding: '7px 10px', fontFamily: 'system-ui,sans-serif' }}>
                <div style={{ fontSize: 10, textTransform: 'uppercase', color: 'var(--text-secondary)', fontWeight: 700 }}>{TYPER[t].label}</div>
                <div style={{ fontSize: 15, fontWeight: 700 }}>{tkr(perTyp[t])} tkr</div>
                <div style={{ fontSize: 10.5, color: 'var(--text-secondary)' }}>
                  {totalt ? Math.round((perTyp[t] / totalt) * 100) : 0} % · {Math.round(perTyp[t] / 12000).toLocaleString('sv-SE')} tkr/mån
                </div>
              </div>
            ))}
          </div>

          <div style={{ fontSize: 12.5, fontFamily: 'system-ui,sans-serif', marginBottom: 10 }}>
            <strong>{tkr(totalt)} tkr</strong> totalt · varav <strong style={{ color: TYPER.fast.farg }}>{tkr(bundet)} tkr bundet</strong> (fast + halvfast) ={' '}
            <strong>{Math.round(bundet / 12000).toLocaleString('sv-SE')} tkr/mån</strong> som rullar oavsett beläggning.
          </div>

          {/* Månadsprofil */}
          <div style={{ overflowX: 'auto', marginBottom: 12 }}>
            <table style={{ borderCollapse: 'collapse', minWidth: 520 }}>
              <thead><tr>
                <th style={{ ...th, textAlign: 'left' }}>Per månad (tkr)</th>
                {MANAD_LABELS.map((m) => <th key={m} style={th}>{m}</th>)}
              </tr></thead>
              <tbody><tr>
                <td style={{ ...td, textAlign: 'left', color: 'var(--text-secondary)' }}>Totalt</td>
                {perManad.map((v, i) => <td key={i} style={{ ...td, fontWeight: 600 }}>{tkr(v)}</td>)}
              </tr></tbody>
            </table>
          </div>

          {/* Kontotabell */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead><tr>
                <th style={{ ...th, textAlign: 'left' }}>Konto</th>
                <th style={{ ...th, textAlign: 'left' }}>Benämning</th>
                <th style={th}>Årstotal</th>
                <th style={th}>Mån</th>
                <th style={th}>Snitt/mån</th>
                <th style={{ ...th, textAlign: 'center' }}>Typ</th>
              </tr></thead>
              <tbody>
                {rader.map((r) => (
                  <React.Fragment key={r.konto}>
                    <tr onClick={() => setOppet(oppet === r.konto ? null : r.konto)}
                      style={{ borderTop: '1px solid var(--border)', cursor: 'pointer', background: oppet === r.konto ? 'var(--surface-2)' : 'transparent' }}>
                      <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>{r.konto}</td>
                      <td style={{ ...td, textAlign: 'left', whiteSpace: 'normal', color: kontoNamn[r.konto] ? 'var(--text)' : 'var(--text-secondary)' }}>
                        {kontoNamn[r.konto] || '(namn saknas i kontoplanen)'}
                      </td>
                      <td style={{ ...td, fontWeight: 700 }}>{kr(r.total)}</td>
                      <td style={{ ...td, color: 'var(--text-secondary)' }}>{r.antal}</td>
                      <td style={{ ...td, color: 'var(--text-secondary)' }}>{kr(r.snitt)}</td>
                      <td style={{ ...td, textAlign: 'center' }}>
                        <button onClick={(e) => { e.stopPropagation(); cyklaTyp(r.konto, r.typ); }}
                          title={`${TYPER[r.typ].label}${r.manuell ? ' (satt för hand)' : ' (föreslagen)'} — klicka för att ändra`}
                          style={{ background: `${TYPER[r.typ].farg}22`, color: TYPER[r.typ].farg, border: `1px solid ${TYPER[r.typ].farg}66`,
                            borderRadius: 20, padding: '2px 9px', fontSize: 10.5, cursor: 'pointer', fontFamily: 'system-ui,sans-serif', fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {TYPER[r.typ].label}{r.manuell ? ' ✓' : ''}
                        </button>
                      </td>
                    </tr>
                    {oppet === r.konto && (
                      <tr style={{ background: 'var(--surface-2)' }}>
                        <td colSpan={6} style={{ padding: '4px 8px 10px' }}>
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ borderCollapse: 'collapse', minWidth: 520 }}>
                              <thead><tr>{MANAD_LABELS.map((m) => <th key={m} style={th}>{m}</th>)}</tr></thead>
                              <tbody><tr>
                                {r.varden.map((v, i) => (
                                  <td key={i} style={{ ...td, color: Math.abs(v) > 100 ? 'var(--text)' : 'var(--text-secondary)' }}>
                                    {Math.abs(v) > 100 ? Math.round(v).toLocaleString('sv-SE') : '–'}
                                  </td>
                                ))}
                              </tr></tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="note">
            Typen föreslås ur månadsmönstret: <strong>Fast</strong> = finns nästan varje månad och ligger jämnt · <strong>Halvfast</strong> = återkommer ofta men ojämnt ·
            <strong> Rörlig</strong> = följer verksamheten · <strong>Engångs</strong> = enstaka tillfällen. Klicka en typ för att ändra den — ✓ betyder satt för hand,
            och den sparas för kontot oavsett år. Klicka en rad för att se månad för månad.
          </div>
        </>
      )}
    </div>
  );
}
